import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import BaseModal from '../../components/common/BaseModal/BaseModal';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';

const ALLOWED_EXTENSIONS = ['sql', 'txt'];

const buildInitialFormData = (jobId, runLabel) => {
    const now = new Date();
    const yymmdd = format(now, 'yyyyMMdd');
    const dateStr = format(now, 'yyyy-MM-dd');
    const timeStr = format(now, 'HH-mm-ss');

    return {
        folderName: '',
        fileNamePrefix: `DBP_${jobId || 'JOBID'}_${runLabel || 'RUNLABEL'}_`,
        fileNameSuffix: `${yymmdd}_migrations.sql`,
        branchName: `DBP_${jobId || 'JOBID'}_${runLabel || 'RUNLABEL'}_${dateStr}_${timeStr}`,
        createPr: false,
    };
};

const PushToRepoModal = ({ isOpen, onClose, onSubmit, isSubmitting, jobId, runLabel }) => {
    const [formData, setFormData] = useState(() => buildInitialFormData(jobId, runLabel));
    const [errors, setErrors] = useState({});

    // Re-initialise pre-fills whenever the modal opens (fresh timestamp each time)
    useEffect(() => {
        if (isOpen) {
            setFormData(buildInitialFormData(jobId, runLabel));
            setErrors({});
        }
    }, [isOpen, jobId, runLabel]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
        }
    };

    const validate = () => {
        const errs = {};
        const { folderName, fileNamePrefix, fileNameSuffix, branchName } = formData;

        // ── Folder Name (optional) ────────────────────────────────────────
        if (folderName.trim()) {
            const folder = folderName.trim();

            if (folder.length > 255) {
                errs.folderName = 'Folder Name exceeds 255 characters';
            } else if (/\s/.test(folder)) {
                errs.folderName = 'Folder Name must not contain spaces';
            } else if (!/^[a-zA-Z0-9/_\-]+$/.test(folder)) {
                errs.folderName = 'Only /, -, _ are allowed';
            } else if (folder.startsWith('/')) {
                errs.folderName = 'Folder Name must not start with /';
            } else if (folder.endsWith('/')) {
                errs.folderName = 'Folder Name must not end with /';
            } else if (/\/\//.test(folder)) {
                errs.folderName = 'Folder Name must not contain consecutive slashes';
            }
        }

        // ── File Name (required) — validate against combined prefix + suffix ──
        const combinedFileName = fileNamePrefix + fileNameSuffix.trim();
        if (!fileNameSuffix.trim()) {
            errs.fileNameSuffix = 'File Name is required';
        } else {
            const file = combinedFileName;

            if (file.length > 255) {
                errs.fileNameSuffix = 'File Name exceeds 255 characters';
            } else if (/\s/.test(fileNameSuffix)) {
                errs.fileNameSuffix = 'File Name must not contain spaces';
            } else if (!/^[a-zA-Z0-9_\-\.]+$/.test(fileNameSuffix.trim())) {
                errs.fileNameSuffix = 'Only -, _, . are allowed';
            } else if (fileNameSuffix.trim().startsWith('.')) {
                errs.fileNameSuffix = 'File Name must not start with a dot';
            } else if (/\.\./.test(file)) {
                errs.fileNameSuffix = 'File Name must not contain consecutive dots';
            } else {
                const dotIndex = file.lastIndexOf('.');
                if (dotIndex !== -1) {
                    const ext = file.slice(dotIndex + 1);
                    if (ext === '') {
                        errs.fileNameSuffix = 'File Name has an incomplete extension. Use .sql or .txt, or remove the dot';
                    } else if (!ALLOWED_EXTENSIONS.includes(ext.toLowerCase())) {
                        errs.fileNameSuffix = 'Only .txt and .sql are allowed';
                    }
                }
                // No extension at all → allowed; backend handles it
            }
        }

        // ── Branch Name (required) ────────────────────────────────────────
        if (!branchName.trim()) {
            errs.branchName = 'Branch Name is required';
        } else {
            const branch = branchName.trim();

            if (branch.length > 200) {
                errs.branchName = 'Branch Name exceeds 200 characters';
            } else if (/\s/.test(branch)) {
                errs.branchName = 'Branch Name must not contain spaces';
            } else if (!/^[a-zA-Z0-9/_\-]+$/.test(branch)) {
                errs.branchName = 'Only /, -, _ are allowed';
            } else if (branch.startsWith('/')) {
                errs.branchName = 'Branch Name must not start with /';
            } else if (branch.endsWith('/')) {
                errs.branchName = 'Branch Name must not end with /';
            } else if (/\/\//.test(branch)) {
                errs.branchName = 'Branch Name must not contain consecutive slashes';
            }
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        onSubmit({
            folderName: formData.folderName.trim(),
            fileName: formData.fileNamePrefix + formData.fileNameSuffix.trim(),
            branchName: formData.branchName.trim(),
            createPr: formData.createPr,
        });
    };

    const handleClose = () => {
        setErrors({});
        onClose();
    };

    const footer = (
        <>
            <button type="button" className="btn btn-cancel" onClick={handleClose} disabled={isSubmitting}>
                Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                Submit
            </button>
        </>
    );

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Push to Repository"
            footer={footer}
            className="push-to-repo-modal"
        >
            <div className="push-to-repo-form">

                {/* ── Folder Name ── */}
                <div className="form-row">
                    <div className="form-group form-group-full">
                        <label>Folder Name</label>
                        <input
                            type="text"
                            placeholder="e.g., migrations/2026"
                            value={formData.folderName}
                            onChange={(e) => handleChange('folderName', e.target.value)}
                            className={errors.folderName ? 'error' : ''}
                        />
                        <ErrorMessage message={errors.folderName} />
                    </div>
                </div>

                {/* ── Branch Name ── */}
                <div className="form-row">
                    <div className="form-group form-group-full">
                        <label>
                            Branch Name <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="e.g., DBP_JOBID_RUN-1_2026-05-07_14-30-00"
                            value={formData.branchName}
                            onChange={(e) => handleChange('branchName', e.target.value)}
                            className={errors.branchName ? 'error' : ''}
                        />
                        <ErrorMessage message={errors.branchName} />
                    </div>
                </div>

                {/* ── File Name (prefix + editable suffix) ── */}
                <div className="form-row">
                    <div className="form-group form-group-full">
                        <label>
                            File Name <span className="required">*</span>
                        </label>
                        <div className={`file-name-input-wrapper${errors.fileNameSuffix ? ' error' : ''}`}>
                            <span className="file-name-prefix">{formData.fileNamePrefix}</span>
                            <input
                                type="text"
                                placeholder="e.g., 20260507_migrations.sql"
                                value={formData.fileNameSuffix}
                                onChange={(e) => handleChange('fileNameSuffix', e.target.value)}
                            />
                        </div>
                        <ErrorMessage message={errors.fileNameSuffix} />
                    </div>
                </div>

                {/* ── Create Pull Request ── */}
                <div className="create-pr-section">
                    <input
                        type="checkbox"
                        checked={formData.createPr}
                        onChange={(e) => handleChange('createPr', e.target.checked)}
                    />
                    <div className="create-pr-text">
                        <label>Create pull request after push</label>
                        <span>A PR will be created from the feature branch into base branch</span>
                    </div>
                </div>

            </div>
        </BaseModal>
    );
};

export default PushToRepoModal;